import React from 'react';
import styled from 'styled-components';
import { Breakpoints, Grid, Type } from '../../../app/StyleGuide';
import { LinkStyleButton, PrimaryButton } from '../../../components/Buttons';
import ErrorMessage from '../../../components/ErrorMessage';
import { Org } from '../../org';
import { ChurchAccount } from '../models/churchAccount';
import { getAccountText } from '../utils/getAccountText';
import { ChurchItem } from './ChurchItem';
import { ButtonRow, Header, Subtitle, Title } from './ConnectChurchAccountModalElements';

const ChurchItems = styled.div`
  display: grid;
  gap: ${Grid._5};
`;

const Section = styled.div`
  display: grid;
  gap: ${Grid._2};
  text-align: left;
`;

const SectionLabel = styled.span`
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.mediumDark};
`;

const AccountAction = styled(LinkStyleButton)`
  position: absolute;
  right: 0;
  bottom: 0;

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    right: ${Grid._3};
    bottom: ${Grid._3};
  }
`;

const NameUpdateMessage = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  font-style: italic;
  font-size: ${Type.Scale._1};
  color: ${Type.Color.medium};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    right: ${Grid._3};
    bottom: ${Grid._3};
  }
`;

type ConnectChurchAccountConfirmationScreenProps = {
  org: Org;
  selectedChurchAccount: ChurchAccount;
  isConnecting: boolean;
  onSelectDifferent: () => void;
  onConnectChurchAccount: () => void;
  errorMessage?: string;
  disableConnectButton?: boolean;
  title?: string;
  subtitle?: string;
};

const getMembersText = (count: number | undefined) => {
  if (!count) return '';
  if (count === 1) return '1 Person';
  return `${count} People`;
};

export const ConnectChurchAccountConfirmationScreen = ({
  org,
  selectedChurchAccount,
  isConnecting,
  onSelectDifferent,
  onConnectChurchAccount,
  errorMessage,
  disableConnectButton = false,
  title,
  subtitle,
}: ConnectChurchAccountConfirmationScreenProps) => (
  <>
    <Header>
      <Title>{title || 'Sync Church Account'}</Title>
      <Subtitle>
        {subtitle ||
          'Sync your existing account profile with your Ministry Grid church to connect your information and improve your experience.'}
      </Subtitle>
    </Header>

    <ChurchItems>
      <Section>
        <SectionLabel>Ministry Grid Church</SectionLabel>
        <ChurchItem name={org.name} logo={org.logo} meta={getMembersText(org.members?.length)}>
          {selectedChurchAccount.name !== org.name && (
            <NameUpdateMessage>Your church name will change to {selectedChurchAccount.name}</NameUpdateMessage>
          )}
        </ChurchItem>
      </Section>

      <Section>
        <SectionLabel>Lifeway Church Account</SectionLabel>
        <ChurchItem name={selectedChurchAccount.name} meta={getAccountText(selectedChurchAccount)}>
          <AccountAction onClick={onSelectDifferent} data-tracking-id="choose-another-church-account">
            Not the right account?
          </AccountAction>
        </ChurchItem>
      </Section>
    </ChurchItems>

    {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

    <ButtonRow>
      <PrimaryButton
        disabled={isConnecting || disableConnectButton}
        operating={isConnecting}
        onClick={onConnectChurchAccount}
        data-tracking-id="sync-church-account-confirm"
      >
        Sync Church Account
      </PrimaryButton>
    </ButtonRow>
  </>
);
